import { Theme } from '@nivo/core';
import { ResponsiveLine } from '@nivo/line';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaCog, FaFilter } from 'react-icons/fa';
import { FiToggleLeft, FiToggleRight } from 'react-icons/fi';

import { FormControl, FormControlLabel, Popover, Radio, RadioGroup, Typography } from '@material-ui/core';
import { AxiosError } from 'axios';
import { addDays } from 'date-fns';
import styled from 'styled-components';
import DateInput from '../../../../../components/DateInput';
import Input from '../../../../../components/Input';
import { loadData } from '../../../../../hooks/loadData';
import { useSelectInfo } from '../../../../../hooks/selectInfo';
import { useToast } from '../../../../../hooks/toast';
import { ISensor } from '../../../../../interfaces';
import api from '../../../../../services/api';
import User from '../../User';
import { BarGraph } from '../components/BarGraph';
import { LineGraph } from '../components/LineGraph';
import * as S from './styles';
import { Button, FilterContainer } from './styles';

const CommandButton = styled.button`
  width: 110px;
  height: 35px;
  background-color: #1b9eb2;
  border: none;
  border-radius: 5px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 14px !important;
  color: white !important;
  padding: 0 8px;
  cursor: pointer;
  font-family: "Montserrat", sans-serif !important;
  font-weight: 500 !important;
  text-transform: none !important;
  line-height: 1 !important;
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: clip !important;
  
  &:hover {
    background-color: #157e8e;
  }
  
  * {
    color: white !important;
    font-size: 14px !important;
  }
`;

// interface ISensorData {
//   id: string;
//   sensor_id: string;
//   value: number;
//   timestamp: string;
//   created_at: string;
//   updated_at: string;
// }

interface IDashboardDataDTO {
  id: string;
  label: string;
  value: number;
  min?: number;
  max?: number;
}

export const style: Theme = {
  axis: {
    ticks: { text: { fill: '#0C5C75', font: '"Montserrat", sans-serif', fontSize: '1.0em' } },
  },
  grid: { line: { stroke: '#d1d1d1', strokeWidth: 1, strokeDasharray: '3 3' } },
  tooltip: { container: { fontSize: '0.7em' } },
  legends: { text: { fontSize: '1em' } },
  labels: { text: { fontSize: '2em' } },
};

type LinearGraphProps = {
  selectedSensor?: ISensor;
};

function getBrazilDateString(date?: Date): string {
  const d = date ? new Date(date) : new Date();
  // Ajusta para o timezone de Brasília (UTC-3)
  d.setUTCHours(d.getUTCHours() - 3);

  return d.toISOString().split('T')[0]+'T00:00';
}

function getBrazilDateStringLive(date?: Date): string {
  const d = date ? new Date(date) : new Date();
  // Ajusta para o timezone de Brasília (UTC-3)
  d.setUTCHours(d.getUTCHours() - 3);

  return d.toISOString().split('T')[0] + 'T23:59';
}


const LinearGraph: React.FC<LinearGraphProps> = ({ selectedSensor }) => {
  const { addToast } = useToast();
  const { selectedSensorId } = useSelectInfo();
  const formRef = useRef<FormHandles>(null);
  const [popUpAnchor, setPopUpAnchor] = useState<Element | null>(null);
  const isPopUpOpen = Boolean(popUpAnchor);
  const [popUpConfigAnchor, setPopUpConfigAnchor] = useState<Element | null>(null);
  const isPopUpConfigOpen = Boolean(popUpConfigAnchor);
  const [live, setLive] = useState(false);
  const [isLiveVisible, setIsLiveVisible] = useState(true);
  const [chartData, setChartData] = useState<IDashboardDataDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMaxVisible, setMaxVisible] = useState(true);

  const [groupBy, setGroupBy] = React.useState<'nenhum' | 'dia' | 'hora' | 'semana' | 'mes'>('nenhum');
  const [valueType, setValueType] = React.useState<'instantaneo' | 'acumulado' | 'minimomaximo'>('instantaneo');
  const [scaleType, setScaleType] = React.useState<'automatico' | 'personalizado'>('automatico');

  const [minScaleValue, setMinScaleValue] = React.useState<number | ''>('');
  const [maxScaleValue, setMaxScaleValue] = React.useState<number | ''>('');
  const [draftMinScaleValue, setDraftMinScaleValue] = React.useState<number | ''>('');
  const [draftMaxScaleValue, setDraftMaxScaleValue] = React.useState<number | ''>('');
  const [maxValueInput, setMaxValueInput] = useState<number | ''>('');

  const currentDate = useMemo(() => getBrazilDateString(), []);
  const initialDate = useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return getBrazilDateString(yesterday);
  }, []);

  const isDesktop = window.innerWidth > 768;
  const maxData = useMemo(() => (isDesktop ? 30 : 15), []);

  const esperar = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  const dataFormat = useMemo(() => {
    console.log('Formatting data:', chartData);
    let _data = chartData.map(curr => ({ x: curr.label, y: curr.value }));
    let report = [];
    if (valueType === 'minimomaximo') {
      let min = { id: 'mínimo', data: chartData.map(curr => ({ x: curr.label, y: curr.min || 0 })) };
      let max = { id: 'máximo', data: chartData.map(curr => ({ x: curr.label, y: curr.max || 0 })) };
      report.push(min, max);
    } else {
      report.push({ id: selectedSensor?.name || 'Sensor', data: _data });
    }
    


    return report;
  }, [chartData, valueType, maxScaleValue, addToast, selectedSensor?.name]);

const toastShownRef = useRef(false);

useEffect(() => {
  if (!dataFormat.length || maxScaleValue === '') return;

  const maxDataY = Math.max(...chartData.map(i => i.value));
  if (maxDataY > maxScaleValue && !toastShownRef.current) {
    addToast({
      type: 'info',
      title: 'Valor máximo excedido',
      description: `Altere o valor máximo da escala ou altere o tipo de escala para automático para melhor visualização.`,
    });
    toastShownRef.current = true;
  }

  const minDataY = Math.min(...chartData.map(i => i.value));
  if (minScaleValue !== '' && minDataY < Number(minScaleValue) && !toastShownRef.current) {
    addToast({
      type: 'info',
      title: 'Valor mínimo excedido',
      description: `Altere o valor mínimo da escala ou altere o tipo de escala para automático para melhor visualização.`,
    });
    toastShownRef.current = true;
  }

}, [chartData, maxScaleValue, addToast]);

// resetar quando mudar sensor ou filtros
useEffect(() => {
  toastShownRef.current = false;
}, [selectedSensorId, valueType, groupBy]);

  const handleGroupBy = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newGroupBy = (event.target as HTMLInputElement).value as 'nenhum' | 'dia' | 'hora' | 'semana' | 'mes';
    setGroupBy(newGroupBy);
    console.log('New groupBy:', newGroupBy);
    fetchData(valueType, newGroupBy);
  };

  const handleValueType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = (event.target as HTMLInputElement).value as 'instantaneo' | 'acumulado' | 'minimomaximo';
    setValueType(newValue);

    let newGroupBy = groupBy;
    if (newValue !== 'instantaneo' && groupBy === 'nenhum') {
      newGroupBy = 'dia'; // Default to 'hora' if not 'instantaneo'
    }
    if (newValue === 'instantaneo') {
      newGroupBy = 'nenhum'; // Reset groupBy to 'nenhum' for instantaneo
    }
    setGroupBy(newGroupBy as 'nenhum' | 'dia' | 'hora' | 'semana' | 'mes'); // Default to 'hora' if not 'instantaneo'
    console.log('New valueType:', newValue, 'Adjusted groupBy:', newGroupBy);
    fetchData(newValue, newGroupBy);
  };

  const handleScaleType = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = (event.target as HTMLInputElement).value as 'automatico' | 'personalizado';
    setScaleType(newValue);

    if (newValue === 'automatico') {
      setDraftMinScaleValue('');
      setDraftMaxScaleValue('');

    }
  };

  const fetchData = useCallback(
    async (valuetypeParam: any, groupbyParam: any) => {
      console.log('Fetching data with params:', { valuetypeParam, groupbyParam });
      standarizeParamsForQuery(valuetypeParam, groupbyParam);
      let max = formRef.current?.getFieldValue('max');

      if (max === false && valuetypeParam === 'instantaneo') {
        max = maxData;
      }
      if (valuetypeParam !== 'instantaneo') {
        max = 0;
      }
      if (live === true) {
        max = maxData;
      }
      const initialDate = formRef.current?.getFieldValue('initialDate');
      const finalDate = formRef.current?.getFieldValue('finalDate');

      loadData(setChartData, addToast, selectedSensorId, initialDate, finalDate, max, valuetypeParam, groupbyParam, setIsLoading);
    },
    [addToast, selectedSensorId, setIsLoading, formRef, valueType, groupBy, live],
  );

  const standarizeParamsForQuery = (valueType: string, groupBy: string) => {
    let initialDate = formRef.current?.getFieldValue('initialDate');
    let finalDate = formRef.current?.getFieldValue('finalDate');
    let differenceInDays = Math.ceil((new Date(finalDate).getTime() - new Date(initialDate).getTime()) / (1000 * 3600 * 24)) - 1;
    let message = '';
    let max = formRef.current?.getFieldValue('max');

    if (valueType === undefined || valueType === 'instantaneo') {
      setIsLiveVisible(true);
    } else {
      setMaxVisible(false);
      setIsLiveVisible(false);
      setLive(false);
    }

    if (live) {
      initialDate = getBrazilDateString();
      finalDate = getBrazilDateStringLive();
      formRef.current?.setFieldValue('initialDate', initialDate);
      formRef.current?.setFieldValue('finalDate', finalDate);
      formRef.current?.setFieldValue('max', maxData);
      setMaxVisible(false);
      return;
    } else if (valueType === 'instantaneo') {
      setMaxVisible(true);
      formRef.current?.setFieldValue('max', max == false || max == 0 || max === undefined ? maxData : max);
    }

    if (isMaxVisible) {

      if (max > 100) {
        max = 100;
        formRef.current?.setFieldValue('max', max);
        addToast({
          type: 'info',
          title: 'Limite de dados',
          description: `O número máximo de dados visíveis foi ajustado para ${max}. Por favor, baixe o arquivo para visualizar os dados completos.`,
        });
      }
    }

    if (isGroupByHour(valueType, groupBy) && initialDate !== finalDate) {
      formRef.current?.setFieldValue('finalDate', initialDate);
      // message = 'O período máximo de consulta para agrupamento por hora é de 1 dia.';
    }

    if (isGroupByDay(valueType, groupBy) && differenceInDays > 60) {
      initialDate = getBrazilDateString(addDays(new Date(finalDate), -60));
      formRef.current?.setFieldValue('initialDate', initialDate);
      message = 'O período máximo de consulta para agrupamento por dia é de 60 dias.';
    }

    if (isGroupByWeek(valueType, groupBy) && differenceInDays > 90) {
      initialDate = getBrazilDateString(addDays(new Date(finalDate), -90));
      formRef.current?.setFieldValue('initialDate', initialDate);
      message = 'O período máximo de consulta para agrupamento por semana é de 90 dias.';
    }

    if (isGroupByMonth(valueType, groupBy) && differenceInDays > 365) {
      initialDate = getBrazilDateString(addDays(new Date(finalDate), -365));
      formRef.current?.setFieldValue('initialDate', initialDate);
      message = 'O período máximo de consulta para agrupamento por mês é de 365 dias.';
    }

    if (message) {
      addToast({
        type: 'info',
        title: 'Período de consulta',
        description: message + ' A data inicial foi ajustada automaticamente.',
      });
    }
  };

  const isGroupByHour = (valueTypeParam: string = valueType, groupByParam: string = groupBy) => {
    return groupByParam === 'hora' && (valueTypeParam === 'acumulado' || valueTypeParam === 'minimomaximo');
  };

  const isGroupByDay = (valueTypeParam: string = valueType, groupByParam: string = groupBy) => {
    return groupByParam === 'dia' && (valueTypeParam === 'acumulado' || valueTypeParam === 'minimomaximo');
  };

  const isGroupByWeek = (valueTypeParam: string = valueType, groupByParam: string = groupBy) => {
    return groupByParam === 'semana' && (valueTypeParam === 'acumulado' || valueTypeParam === 'minimomaximo');
  };

  const isGroupByMonth = (valueTypeParam: string = valueType, groupByParam: string = groupBy) => {
    return groupByParam === 'mes' && (valueTypeParam === 'acumulado' || valueTypeParam === 'minimomaximo');
  };

  const handleBlurValidation = () => {
    if (
      scaleType === 'personalizado' &&
      draftMinScaleValue !== '' &&
      draftMaxScaleValue !== '' &&
      draftMinScaleValue >= draftMaxScaleValue
    ) {
      addToast({
        type: 'error',
        title: 'Valores inválidos',
        description: 'O valor mínimo deve ser menor que o valor máximo.',
      });
    }
  };

  useEffect(() => {
    if (live) {
      // standarizeParamsForQuery(valueType, groupBy);
      console.log('Live mode enabled, starting interval fetch');
      fetchData(valueType, groupBy);
      console.log('First fetch:', { valueType, groupBy });
      const interval = setInterval(fetchData, 2500);
      return () => clearInterval(interval);
    } else {
      console.log('Live mode disabled');
      fetchData(valueType, groupBy);
    }
  }, [live, isMaxVisible, selectedSensorId, addToast, valueType, groupBy, fetchData]);

  useEffect(() => {
    if (selectedSensorId) {
      handleSubmit();
    }
  }, [selectedSensorId]);

  const fetchAndSetScaleConfig = useCallback(async (sensorId: string) => {
    try {
      console.log('Attempting to fetch sensor details for sensorId:', sensorId);
      const response = await api.get<ISensor>('/sensors', { params: { sensor_id: sensorId } });

      const sensor = response?.data;

      if (!sensor) {
        console.error('Sensor data is null or undefined');
        addToast({
          type: 'error',
          title: 'Erro ao buscar dados do sensor',
          description: 'Os dados do sensor não foram encontrados.',
        });
        return;
      }

      if (sensor.automatic_scale === true) {
        setScaleType('automatico');
      } else if (sensor.automatic_scale === false) {
        setScaleType('personalizado');
      }

      if (typeof sensor.scale_min !== 'undefined') {
        const minVal = sensor.scale_min !== null ? sensor.scale_min : '';
        setMinScaleValue(minVal);
        setDraftMinScaleValue(minVal);
      }
      if (typeof sensor.scale_max !== 'undefined') {
        const maxVal = sensor.scale_max !== null ? sensor.scale_max : '';
        setMaxScaleValue(maxVal);
        setDraftMaxScaleValue(maxVal);
        setMaxValueInput(maxVal);
      }
    } catch (err) {
      if ((err as AxiosError).isAxiosError) {
        console.error('Axios error details:', (err as AxiosError).response);
      } else {
        console.error('Unexpected error:', err);
      }
      addToast({
        type: 'error',
        title: 'Erro ao buscar dados do sensor',
        description: 'Não foi possível buscar os dados do sensor. Verifique a conexão ou tente novamente.',
      });
    }
  }, []);

  useEffect(() => {
    if (!selectedSensorId) {
      console.log('selectedSensorId is not set, fetchAndSetScaleConfig will not execute.');
      return;
    }
    
    fetchAndSetScaleConfig(selectedSensorId);
  }, [selectedSensorId, fetchAndSetScaleConfig]);

  const handleOpenConfigPopUp = (event: React.MouseEvent<Element, MouseEvent>) => {
    setPopUpConfigAnchor(event.currentTarget);

    if (selectedSensorId && (minScaleValue === '' || maxScaleValue === '')) {
      fetchAndSetScaleConfig(selectedSensorId);
    }
    setDraftMinScaleValue(minScaleValue !== '' ? minScaleValue : '');
    setDraftMaxScaleValue(maxScaleValue !== '' ? maxScaleValue : '');
  };

  const handleOpenFilterPopUp = (event: React.MouseEvent<Element, MouseEvent>) => {
    setPopUpAnchor(event.currentTarget);
    setMaxValueInput(maxScaleValue !== '' ? maxScaleValue : '');
  };

  const handleSubmit = useCallback(async () => {

    let initialDate = formRef.current?.getFieldValue('initialDate');
    let finalDate = formRef.current?.getFieldValue('finalDate');

    if (initialDate && finalDate && new Date(finalDate) < new Date(initialDate)) {
      if (groupBy !== 'hora') {
        addToast({
          type: 'info',
          title: 'Data inválida',
          description: 'A data final não pode ser menor que a data inicial. A data final foi ajustada automaticamente.',
        });
      }
      formRef.current?.setFieldValue('finalDate', initialDate);
      return;
    }

    if (selectedSensor?.is_cumulative == false && valueType === 'acumulado') {
      setValueType('instantaneo');
      setGroupBy('nenhum');

      // standarizeParamsForQuery('instantaneo', 'nenhum');
      console.log('Value type "acumulado" is not valid for this sensor. Reverting to "instantaneo".');
       toastShownRef.current = false; // <<< reset para permitir exibir de novo
      fetchData('instantaneo', 'nenhum');
    } else {
      // standarizeParamsForQuery(valueType, groupBy);
       toastShownRef.current = false; // <<< reset para permitir exibir de novo
      console.log('Submitting with params:', { valueType, groupBy });
      fetchData(valueType, groupBy);
    }
    handleClosePopUp();
  }, [selectedSensorId, addToast, valueType, groupBy, fetchData]);

  const handleChangeLiveMode = () => setLive(prevLive => !prevLive);
  const handleClosePopUp = () => setPopUpAnchor(null);
  const handleCloseConfigPopUp = () => setPopUpConfigAnchor(null);

  const handleUpdateSensorLimits = async (scaleType: 'automatico' | 'personalizado') => {
    if (!selectedSensorId) return;

    if(scaleType === 'personalizado' && (draftMinScaleValue === '' || draftMaxScaleValue === '')) {
      addToast({
        type: 'error',
        title: 'Limites inválidos',
        description: 'Por favor, defina valores para os limites mínimo e máximo.',
      });
      return;
    }

    const dataToSend = {
      automatic_scale: scaleType === 'automatico',
      scale_min: scaleType === 'automatico' ? 0 : draftMinScaleValue,
      scale_max: scaleType === 'automatico' ? 0 : draftMaxScaleValue,
    };

    try {
      await api.put(`/sensors/${selectedSensorId}/scale`, dataToSend);

      addToast({
        type: 'success',
        title: 'Limites atualizados',
        description: `Os limites foram atualizados para ${scaleType === 'automatico' ? 'Automático' : 'Personalizado'}.`,
      });

      if (scaleType === 'automatico') {
        setMinScaleValue('');
        setMaxScaleValue('');
        setDraftMinScaleValue('');
        setDraftMaxScaleValue('');
      } else {
        setMinScaleValue(draftMinScaleValue);
        setMaxScaleValue(draftMaxScaleValue);
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao atualizar limites',
        description: (error as AxiosError).response
          ? `${(error as AxiosError).response?.data.message}`
          : 'Erro inesperado ao atualizar os limites.',
      });
    }
  };

  const handleToggleCommand = useCallback(
    async (command: 'on' | 'off') => {
      try {
        if (!selectedSensor?.device_id || selectedSensor?.port === undefined || selectedSensor?.port === null) {
          addToast({
            type: 'error',
            title: 'Erro ao enviar comando',
            description: !selectedSensor?.device_id
              ? 'ID do dispositivo não encontrado'
              : 'Porta do sensor não encontrada',
          });
          return;
        }

        const commandData = command === 'on' ? 'false' : 'true';

        await api.post(`/devices/${selectedSensor.device_id}/${selectedSensor.id}/send-command`, {
          port: Number(selectedSensor.port),
          commandName: 'turnSensorOnOff',
          params: {
            command: commandData,
          },
          retain: true,
        });

        addToast({
          type: 'success',
          title: `Comando ${command === 'on' ? 'Abrir' : 'Fechar'} enviado!`,
          description: 'Comando enviado para o dispositivo com sucesso!',
        });
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao enviar comando',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast, selectedSensor],
  );

  const getGraphScaleValues = () => {
    const min: number | 'auto' = minScaleValue !== '' ? minScaleValue : 'auto';
    const max: number | 'auto' = maxScaleValue !== '' ? maxScaleValue : 'auto';
    return { min, max };
  };

  const { min, max } = getGraphScaleValues();

  const graph = useMemo(() => {
    return (
      <ResponsiveLine
        data={[{ id: User.name, data: dataFormat }]}
        margin={{ top: 25, right: 45, bottom: 92, left: 50 }}
        axisTop={null}
        axisRight={null}
        curve="linear"
        lineWidth={2}
        theme={style}
        yScale={{
          type: 'linear',
          min,
          max,
          stacked: false,
          reverse: false,
        }}
        axisBottom={{
          orient: 'bottom',
          tickSize: 0,
          legendOffset: 40,
          legendPosition: 'middle',
          tickRotation: 75,
        }}
        axisLeft={{
          orient: 'left',
          tickPadding: 12,
          legendOffset: 10,
          tickSize: 0,
          legendPosition: 'middle',
          legend: selectedSensor?.unit_of_measurement || '',
        }}
        markers={[
          {
            axis: 'y',
            value: 0,
            lineStyle: { stroke: '#0C5C75', strokeWidth: 1 },
          },
          {
            axis: 'x',
            value: 0,
            lineStyle: { stroke: '#0C5C75', strokeWidth: 1 },
          },
        ]}
        colors={['#FFB30D', '#00CFEF', '#0C5C75', '#e3eff4']}
        enableSlices={false}
        areaOpacity={0.2}
        useMesh
        motionStiffness={100}
        enableGridY={false}
        enableGridX
        enablePoints
        yFormat=">-.3f"
      />
    );
  }, [dataFormat, selectedSensor, minScaleValue, maxScaleValue]);

  return (
    <S.Container>
      <S.Content>
        {/* <S.GrafContainer>{graph}</S.GrafContainer> */}
        <S.GrafContainer>

          {((valueType === 'acumulado' && selectedSensor?.is_cumulative && chartData.length === 0) ||
            (valueType !== 'acumulado' && (!dataFormat[0] || dataFormat[0].data.length === 0))) ? (
            <div style={{ width: '100%', textAlign: 'center', padding: '2rem', color: '#0c5c75', fontWeight: 600 }}>
              Nenhum dado encontrado para o filtro selecionado.
            </div>
          ) : valueType === 'acumulado' && selectedSensor?.is_cumulative ? (
            <BarGraph data={chartData} axis={{ bottomLegend: '', leftLegend: "[ " + (selectedSensor?.unit_of_measurement || '') + " ]" }} />
          ) : (
            <LineGraph
              id={selectedSensor?.name || ''}
              data={dataFormat}
              axis={{ bottomLegend: '', leftLegend: "[ " + (selectedSensor?.unit_of_measurement || '') + " ]" }}
              yMin={min}
              yMax={max}
              showLiveLegend={live}
              unitName={selectedSensor?.unit_of_measurement || ''}
              sensorName={selectedSensor?.name || ''}
            />
          )}

        </S.GrafContainer>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <Popover
            open={isPopUpOpen}
            anchorEl={popUpAnchor}
            onClose={handleClosePopUp}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <S.FiltersPopUp>
              <strong>Filtros</strong>
              <section>
                {isMaxVisible && (
                  <>
                    <div>
                      <h3>Máximo:</h3>
                      <Input containerClassName="max-input" name="max" type="number" max={100} defaultValue={formRef.current?.getFieldValue('max')} />
                    </div>
                  </>
                )}

                <div className="date-input">
                  <h3>Inicial:</h3>
                  <DateInput name="initialDate" type="date" defaultValue={formRef.current?.getFieldValue('initialDate')} />
                </div>
                <div className="date-input">
                  <h3>Final:</h3>
                  <DateInput name="finalDate" type="date" defaultValue={formRef.current?.getFieldValue('finalDate')} />
                </div>
                <Button onClick={handleSubmit} className="sendMessageButton">
                  Aplicar
                </Button>
              </section>
            </S.FiltersPopUp>
          </Popover>
          <Popover
            open={isPopUpConfigOpen}
            anchorEl={popUpConfigAnchor}
            onClose={handleCloseConfigPopUp}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <S.FiltersPopUp style={{ height: '590px', maxHeight: '590px', overflowY: 'auto' }}>
              <FormControl component="fieldset">
                <strong>Agrupar Por</strong>
                <RadioGroup aria-label="group by" name="rgpGroupBy" value={groupBy} onChange={handleGroupBy}>
                  <FormControlLabel
                    value="nenhum"
                    disabled={valueType !== 'instantaneo'}
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Nenhum</Typography>}
                  />
                  <FormControlLabel
                    value="hora"
                    disabled={valueType === 'instantaneo'}
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Hora</Typography>}
                  />
                  <FormControlLabel
                    value="dia"
                    disabled={valueType === 'instantaneo'}
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Dia</Typography>}
                  />
                  <FormControlLabel
                    value="semana"
                    disabled={valueType === 'instantaneo'}
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Semana</Typography>}
                  />
                  <FormControlLabel
                    value="mes"
                    disabled={valueType === 'instantaneo'}
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Mês</Typography>}
                  />
                </RadioGroup>
              </FormControl>
              <FormControl component="fieldset">
                <strong>Valor</strong>
                <RadioGroup aria-label="Valor" name="rgpValor" value={valueType} onChange={handleValueType}>
                  <FormControlLabel
                    value="instantaneo"
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Instântaneo</Typography>}
                  />
                  <FormControlLabel
                    value="acumulado"
                    disabled={!selectedSensor?.is_cumulative}
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Acumulado</Typography>}
                  />
                  <FormControlLabel
                    value="minimomaximo"
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Mínimo e Máximo</Typography>}
                  />
                </RadioGroup>
              </FormControl>
              <FormControl component="fieldset">
                <strong>Escala</strong>
                <RadioGroup aria-label="Escala" name="rgpEscala" value={scaleType} onChange={handleScaleType}>
                  <FormControlLabel
                    value="automatico"
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Automático</Typography>}
                    onChange={() => handleUpdateSensorLimits('automatico')}
                  />
                  <FormControlLabel
                    value="personalizado"
                    control={<Radio color="primary" />}
                    label={<Typography style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300 }}>Personalizado</Typography>}
                  />
                </RadioGroup>

                <div style={{}}>
                  {scaleType === 'personalizado' && (
                    <div style={{ marginTop: '1rem' }}>
                      <div>
                        <label htmlFor="minValue">Mínimo:</label>
                        <Input
                          id="minValue"
                          name="minValue"
                          type="number"
                          value={draftMinScaleValue !== '' ? String(draftMinScaleValue) : ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftMinScaleValue(e.target.value !== '' ? Number(e.target.value) : '')}
                        />
                      </div>
                      <div style={{ marginTop: '1rem' }}>
                        <label htmlFor="maxValue">Máximo:</label>
                        <Input
                          id="maxValue"
                          name="maxValue"
                          type="number"
                          value={draftMaxScaleValue !== '' ? String(draftMaxScaleValue) : ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftMaxScaleValue(e.target.value !== '' ? Number(e.target.value) : '')}
                          disabled={draftMinScaleValue === ''}
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}> {}
                        <Button
                          type="button"
                          style={{ width: '80px' }}
                          onClick={() => {
                            if (
                              scaleType === 'personalizado' &&
                              draftMinScaleValue !== '' &&
                              draftMaxScaleValue !== '' &&
                              draftMinScaleValue >= draftMaxScaleValue
                            ) {
                              addToast({
                                type: 'error',
                                title: 'Valores inválidos',
                                description: 'O valor mínimo deve ser menor que o valor máximo.',
                              });
                              return;
                            }
                            handleUpdateSensorLimits('personalizado');
                          }}
                        >
                          Salvar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </FormControl>

            </S.FiltersPopUp>
          </Popover>
          <div className="mobile-row">
            {isLiveVisible && (
              <S.LiveButton type="button" live={live} onClick={handleChangeLiveMode}>
                <div className="live-icon" />
                Ao vivo
              </S.LiveButton>
            )}
            {selectedSensor?.enable_commands && (
              <div style={{ display: 'flex', gap: '0.5px' }}>
                <CommandButton
                  type="button"
                  onClick={() => handleToggleCommand('on')}
                >
                  <FiToggleRight size="1em" color="#fff" />
                  Abrir
                </CommandButton>
                <CommandButton
                  type="button"
                  onClick={() => handleToggleCommand('off')}
                >
                  <FiToggleLeft size="1em" color="#fff" />
                  Fechar
                </CommandButton>
              </div>
            )}
            <Button className="mobile-filters-button" onClick={handleOpenFilterPopUp} type="button">
              <FaFilter size="1em" color="#fff" />
              Filtros
            </Button>
            <Button className="mobile-filters-button" type="button" onClick={handleOpenConfigPopUp}>
              <FaCog size="1.5em" color="#fff" />
              Configurações
            </Button>
          </div>
          <FilterContainer>
            {isLoading && <S.LoadingSpinner size={20} />}
            <div className="filterLegends">
              <section>
                {isLiveVisible && (
                  <>
                    <S.LiveButton type="button" live={live} onClick={handleChangeLiveMode}>
                      <div className="live-icon" />
                      Ao vivo
                    </S.LiveButton>
                  </>
                )}
                {isMaxVisible && (
                  <>
                    <div>
                      <h3>Máximo:</h3>
                      <Input containerClassName="max-input" name="max" type="number" max={100} defaultValue={maxData} />
                    </div>
                  </>
                )}
                <div>
                  <h3>{isGroupByHour() || live ? 'Data' : 'Inicial:'}</h3>
                  <DateInput name="initialDate" disabled={live} type="date" defaultValue={initialDate} />
                </div>
                <S.FinalDateInput hidden={isGroupByHour() || live}>
                  <h3>Final:</h3>
                  <DateInput name="finalDate" type="date" defaultValue={currentDate} />
                </S.FinalDateInput>
                {selectedSensor?.enable_commands && (
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <CommandButton
                      type="button"
                      onClick={() => handleToggleCommand('on')}
                    >
                      <FiToggleRight size="1em" color="#fff" />
                      Abrir
                    </CommandButton>
                    <CommandButton
                      type="button"
                      onClick={() => handleToggleCommand('off')}
                    >
                      <FiToggleLeft size="1em" color="#fff" />
                      Fechar
                    </CommandButton>
                  </div>
                )}
                <Button type='button' onClick={handleSubmit} name='sendMessageButton' className="sendMessageButton">
                  <FaFilter size="1.5em" color="#fff" />
                </Button>
                <Button onClick={event => setPopUpConfigAnchor(event.currentTarget)} className="settingsButton" type="button">
                  <FaCog size="1.5em" color="#fff" />
                </Button>
              </section>
            </div>
          </FilterContainer>
        </Form>
      </S.Content>
    </S.Container>
  );
};

export default LinearGraph;